import React, { useState, useMemo, lazy, Suspense } from "react";
import InfoHint from "../components/InfoHint";
import PasswordInput from "../components/PasswordInput";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { BookOpen, Play, ShieldCheck, Sparkle } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
// Lazy-loaded so the heavy face-api.js library (~1 MB) only downloads
// when a user actually opens the Face Login panel — not on every visit.
const FaceLoginPanel = lazy(() => import("../components/FaceLoginPanel"));

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/33c49a49-4b5d-43fb-a2a2-4b27681abf6a/images/100e04a3fadaf704cc02264fad3969b261c773aa0b60913c93c05067c977e5d3.png";

// Map preferred-method tokens to human labels for the highlight chip.
const PREFERRED_LABEL = {
  email: "email",
  google: "Google",
  face: "your face",
};

export default function Login() {
  const { login, loginWithGoogle, setUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [faceOpen, setFaceOpen] = useState(false);

  // Recall the user's last sign-in method (set on every successful auth
  // in AuthContext). Read once on mount so toggling between methods
  // during a single visit doesn't re-arrange the page mid-flow.
  const preferred = useMemo(() => {
    try {
      const p = window.localStorage.getItem("hw_preferred_signin");
      return ["email", "google", "face"].includes(p) ? p : null;
    } catch {
      return null;
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/bookmarks");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid grid-cols-1 lg:grid-cols-2 animate-fade-up">
      <div className="relative hidden lg:block">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-forest/85 to-forest/40" />
        <div className="absolute bottom-12 left-12 right-12 text-cream">
          <BookOpen size={40} weight="duotone" />
          <p className="font-heading text-4xl mt-6 italic leading-tight">"Thy word is a lamp unto my feet, and a light unto my path."</p>
          <p className="text-cream/70 text-sm mt-3">Psalm 119:105</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex items-center gap-2.5 flex-wrap"><h1 className="font-heading text-4xl text-forest font-medium">{t("auth.welcomeBack")}</h1><InfoHint testid="login-info">{t("auth.signInSubtitle")}</InfoHint></div>

          {/* Preferred-method hint chip — surfaces last-used flow so
              returning users sign in with a single tap. Hidden on the
              first visit (no preferred method stored yet) so the
              default email-first layout stays unchanged for first-
              time visitors. */}
          {preferred && (
            <div
              data-testid="preferred-signin-hint"
              className="mt-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-forest/8 text-forest text-[11px]"
            >
              <Sparkle size={12} weight="fill" />
              You last signed in with {PREFERRED_LABEL[preferred]}
            </div>
          )}

          {/* Google-first layout when Google was the last-used method.
              Renders the Continue with Google button up top, full-
              width and emphasized; the email form drops below an
              "Or use email" divider. Identical to the existing
              layout when preferred !== 'google'. */}
          {preferred === "google" && (
            <>
              <button
                type="button"
                onClick={loginWithGoogle}
                data-testid="google-signin-button-primary"
                className="mt-5 w-full h-12 text-forest hover:text-terracotta text-sm font-medium transition-all hover:scale-[1.02] flex items-center justify-center gap-2.5"
              >
                <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" opacity=".95"/><path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" opacity=".95"/><path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09 0-.72.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" opacity=".95"/><path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" opacity=".95"/></svg>
                Continue with Google
              </button>
              <div className="my-5 flex items-center gap-3">
                <div className="flex-1 h-px bg-cream-300" />
                <span className="text-[10px] tracking-[0.2em] uppercase text-muted_ink">Or use email</span>
                <div className="flex-1 h-px bg-cream-300" />
              </div>
            </>
          )}

          <label className="block mt-7 text-[10px] tracking-[0.2em] uppercase text-muted_ink font-semibold">{t("auth.email")}</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="login-email-input"
            className="mt-1 w-full h-12 px-4 rounded-xl bg-white border border-cream-300 outline-none focus:border-forest text-sm"
          />

          <label className="block mt-4 text-[10px] tracking-[0.2em] uppercase text-muted_ink font-semibold">{t("auth.password")}</label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            testId="login-password-input"
            className="mt-1 w-full h-12 px-4 rounded-xl bg-white border border-cream-300 outline-none focus:border-forest text-sm"
          />

          {error && <p className="text-red-600 text-xs mt-3" data-testid="login-error">{error}</p>}

          <div className="mt-2 text-right">
            <Link to="/forgot-password" data-testid="login-forgot-password" className="text-xs text-terracotta hover:underline">{t("auth.forgotPassword")}</Link>
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className={`mt-6 w-full h-12 rounded-full text-cream text-sm font-medium transition-all hover:scale-[1.02] disabled:opacity-50 ${
              preferred === "email" || !preferred
                ? "bg-forest hover:bg-forest-600"
                : "bg-forest/85 hover:bg-forest"
            }`}
          >
            {busy ? t("auth.signingIn") : t("nav.signIn")}
          </button>

          {/* Google + Face block. We hide the lower Google button when
              we've already rendered Google at the TOP for last-Google
              users (preferred === 'google'). Otherwise the lower
              section is unchanged so first-time visitors keep the
              email-first layout we already shipped. */}
          {preferred !== "google" && (
            <>
              <div className="my-5 flex items-center gap-3">
                <div className="flex-1 h-px bg-cream-300" />
                <span className="text-[10px] tracking-[0.2em] uppercase text-muted_ink">{t("auth.or")}</span>
                <div className="flex-1 h-px bg-cream-300" />
              </div>

              <button
                type="button"
                onClick={loginWithGoogle}
                data-testid="google-signin-button"
                className="w-full h-12 rounded-full bg-white border border-cream-300 hover:border-forest text-ink text-sm font-medium transition-all hover:scale-[1.02] flex items-center justify-center gap-2.5"
              >
                <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09 0-.72.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                {t("auth.continueWithGoogle")}
              </button>
            </>
          )}

          <button
            type="button"
            onClick={() => {
              if (!email.trim()) { setError("Enter your email first to sign in with your face."); return; }
              setError("");
              setFaceOpen(true);
            }}
            data-testid="face-signin-button"
            className={`${preferred === "google" ? "mt-5" : "mt-3"} w-full h-12 rounded-full bg-cream-100 border border-forest/15 hover:border-forest text-ink text-sm font-medium transition-all hover:scale-[1.02] flex items-center justify-center gap-2.5`}
          >
            <ShieldCheck size={18} weight="fill" className="text-forest" />
            Sign in with Face
          </button>

          <p className="text-sm text-muted_ink mt-6">
            No account yet?{" "}
            <Link to="/register" data-testid="login-to-register" className="text-terracotta hover:underline">
              Create one
            </Link>
          </p>

          <Link
            to="/tour"
            data-testid="login-watch-tour-button"
            className="mt-4 w-full h-12 rounded-full bg-terracotta text-cream text-sm font-medium transition-all hover:scale-[1.02] hover:bg-[#a64f31] flex items-center justify-center gap-2.5"
          >
            <Play size={16} weight="fill" />
            New here? Watch the tour
          </Link>
        </form>
      </div>

      {faceOpen && (
        <Suspense fallback={null}>
          <FaceLoginPanel
            email={email}
            onClose={() => setFaceOpen(false)}
            onSuccess={(data) => {
              setFaceOpen(false);
              setUser(data);
              try { window.localStorage.setItem("hw_preferred_signin", "face"); } catch {}
              navigate("/bookmarks");
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
